// Looks up a freshly-picked SAB client and projects it into the persisted
// global selection shape. Done here (rather than in the modal) so the modal
// stays caller-agnostic — pages that just want a one-shot selection don't
// need a displayName, while the global header chip does.
import {
	type SabClientListItemDto,
	SabClientType,
} from "Infrastructure/Api/generated/types.gen";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
	LaunchModeModal,
	type LaunchModeSelection,
} from "Modeling/Components/LaunchModeModal";
import { Pencil, User } from "Shared/appIcons";
import { useSelectedClient } from "Shared/Client/SelectedClientContext";
import { clientDisplayName } from "Shared/Client/selectedClientStorage";
import { SimpleTooltip } from "Shared/SimpleTooltip";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const ClientHeaderSwitcher = () => {
	const { t } = useResource();
	const { client, setClient, clearClient } = useSelectedClient();
	const [open, setOpen] = useState(false);
	// The picker inside LaunchModeModal returns a SabClientListItemDto via its
	// own callback; the modal then forwards only the ids. To capture the
	// displayName we listen on the same modal but reach into the picker's
	// onPick by passing a wrapper handler — implemented via the
	// `onPickRaw` extension below in LaunchModeModal.
	const [lastPicked, setLastPicked] = useState<SabClientListItemDto | null>(
		null,
	);

	const handleLaunch = (selection: LaunchModeSelection) => {
		if (selection.mode === "without-client") {
			clearClient();
			setOpen(false);
			return;
		}
		// Prefer the captured row (has the full name + type); fall back to the
		// identification number when, for some reason, the row wasn't captured.
		const capturedRow =
			lastPicked !== null && lastPicked.sabClientID === selection.sabClientID
				? lastPicked
				: null;
		const displayName =
			capturedRow !== null
				? clientDisplayName(capturedRow)
				: selection.sabIdentificationNumber;
		setClient({
			sabClientID: selection.sabClientID,
			sabIdentificationNumber: selection.sabIdentificationNumber,
			displayName,
			clientType: capturedRow?.type ?? SabClientType.PhysicalPerson,
		});
		setLastPicked(null);
		setOpen(false);
	};

	return (
		<>
			<SimpleTooltip
				content={
					client !== null
						? t(Resources.Modeling.LaunchMode.Switcher.Change)
						: t(Resources.Modeling.LaunchMode.Switcher.NoClient)
				}
			>
				<Button
					type="button"
					variant="ghost"
					onClick={() => setOpen(true)}
					className="group text-caption text-muted-foreground hover:text-primary inline-flex h-auto items-center gap-1 px-0 py-0 font-normal hover:bg-transparent"
				>
					<User
						className={cn(
							"size-3 flex-shrink-0",
							client !== null ? "text-primary" : "text-muted-foreground",
						)}
					/>
					{client !== null ? (
						<>
							<span>
								{t(Resources.Modeling.LaunchMode.Switcher.SelectedPrefix)}:
							</span>
							<span className="text-foreground max-w-[200px] truncate font-medium">
								{client.displayName}
							</span>
							<Pencil className="size-3 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
						</>
					) : (
						<>
							<span>{t(Resources.Modeling.LaunchMode.Switcher.NoClient)}</span>
							<span className="text-muted-foreground">·</span>
							<span className="text-primary font-medium">
								{t(Resources.Modeling.LaunchMode.Switcher.SelectAction)}
							</span>
						</>
					)}
				</Button>
			</SimpleTooltip>
			<LaunchModeModal
				open={open}
				onOpenChange={setOpen}
				onLaunch={handleLaunch}
				onPickClient={setLastPicked}
				title={t(Resources.Modeling.LaunchMode.Switcher.Title)}
				description={t(Resources.Modeling.LaunchMode.Description)}
			/>
		</>
	);
};
